// Boost Blenz Admin — permission model (shared by the Worker and the SPA).
//
// A user has ONE role (owner / ops / support) that sets their DEFAULT permissions.
// The super-admin can then override any single permission for an individual user
// without affecting anyone else — grant one that the role doesn't include, or
// revoke one it does. Effective = role default, unless a per-user override exists.
//
// Permission keys map 1:1 to the screens in the sidebar, plus `manage_users`
// (access to the Users settings screen + the right to edit people). The UI hiding
// a screen is never the security — every protected API also checks server-side.

export type Role = "owner" | "ops" | "support";

export type Permission = {
  key: string;
  label: string;
  group: string; // matches the sidebar section, for grouping in the Users UI
};

// The catalog — order + grouping mirror the sidebar.
export const PERMISSIONS: Permission[] = [
  { key: "pulse", label: "Daily Pulse", group: "Overview" },
  { key: "decision", label: "Decision Dashboard", group: "Overview" },
  { key: "forecasting", label: "Forecasting", group: "Overview" },
  { key: "books", label: "Books", group: "Overview" },
  { key: "insights", label: "Insights", group: "Overview" },
  { key: "profit", label: "Profit", group: "Finances" },
  { key: "unit", label: "Per-Unit Cost", group: "Finances" },
  { key: "allocate", label: "Profit-First", group: "Finances" },
  { key: "expenses", label: "Expenses", group: "Finances" },
  { key: "replacements", label: "Replacement Losses", group: "Finances" },
  { key: "newstuff", label: "New Stuff", group: "Finances" },
  { key: "ads", label: "Ad Performance", group: "Marketing" },
  { key: "seo", label: "SEO", group: "Marketing" },
  { key: "email", label: "Email Marketing", group: "Marketing" },
  { key: "quiz", label: "Quiz", group: "Marketing" },
  { key: "kpi", label: "KPI Targets", group: "Marketing" },
  { key: "health", label: "App Health", group: "App" },
  { key: "customers", label: "Customers", group: "App" },
  { key: "revenue", label: "In-App Sales", group: "App" },
  { key: "content", label: "Content", group: "App" },
  { key: "appcopy", label: "App Copy (edit the app's wording)", group: "App" },
  { key: "push", label: "Messaging: post & push notices", group: "App" },
  // "suppression" removed 2026-09-10 with its screen. A customer's notification off switch is
  // on their own record now, under Customers, so it rides on the "customers" permission.
  // Opens the ERP (erp.boostblenzhq.com). The ERP asks the admin over its private bridge
  // whether a signed-in person has this permission, and lets them straight in if so.
  { key: "inventory", label: "Inventory Hub (ERP)", group: "Operations" },
  // Opens the Help Desk CRM (help.boostblenzhq.com) the same way — one login, no second invite.
  { key: "crm", label: "Help Desk CRM", group: "Operations" },
  { key: "helpdesk", label: "Help Desk", group: "Customer Service" },
  { key: "kb", label: "Knowledge Base", group: "Customer Service" },
  { key: "integrations", label: "Integrations", group: "Settings" },
  { key: "security", label: "Security", group: "Settings" },
  { key: "manage_users", label: "Manage users & roles", group: "Settings" },
];

export const PERMISSION_KEYS: string[] = PERMISSIONS.map((p) => p.key);

// Default permission set per role. Owner = everything. Ops = operations, finance
// & marketing, but not Security or user management. Support = customer service only.
export const ROLE_DEFAULTS: Record<Role, string[]> = {
  owner: PERMISSION_KEYS.slice(),
  ops: ["pulse", "decision", "forecasting", "books", "insights", "profit", "unit", "allocate", "replacements", "newstuff", "ads", "seo", "email", "quiz", "kpi", "health", "customers", "revenue", "content", "appcopy", "push", "integrations", "inventory"],
  // Support also sees App Health + Customers (read every number, open every profile, use the
  // fix-it actions) — Claude Design handoff 2026-09-13, SPEC §2/§5.12. Messaging is read-only for
  // them via `customers`; posting a notice still needs `push`. App Copy stays locked.
  support: ["helpdesk", "kb", "health", "customers", "crm"],
};

// The separate tools a dashboard login also opens. Each has its own role inside it; the
// permission above is the on/off switch, the level below is what they can do there.
export const APP_KEYS = ["inventory", "crm"] as const;

export type ErpRole = "owner" | "manager" | "staff" | "returns";
export type CrmRole = "owner" | "lead" | "agent";

export const ERP_LEVELS: { key: ErpRole; label: string; sub: string }[] = [
  { key: "manager", label: "Manager", sub: "Everything except the team. Sees costs." },
  { key: "staff", label: "Staff", sub: "Makes batches, checks in deliveries. Never sees money." },
  { key: "returns", label: "3PL returns", sub: "Only the Returns screens." },
];
export const CRM_LEVELS: { key: CrmRole; label: string; sub: string }[] = [
  { key: "lead", label: "Lead", sub: "Approves refunds, edits snippets, merges customers." },
  { key: "agent", label: "Agent", sub: "Answers customers." },
];

// Owner is always owner in every tool. Otherwise a stored level wins; no level = the old
// mapping (Ops → ERP Manager, Support → ERP Staff; everyone → CRM Agent).
export function erpRoleFor(role: Role, stored?: string | null): ErpRole {
  if (role === "owner") return "owner";
  if (stored && ERP_LEVELS.some((l) => l.key === stored)) return stored as ErpRole;
  return role === "ops" ? "manager" : "staff";
}
export function crmRoleFor(role: Role, stored?: string | null): CrmRole {
  if (role === "owner") return "owner";
  if (stored && CRM_LEVELS.some((l) => l.key === stored)) return stored as CrmRole;
  return "agent";
}

export const ROLE_LABELS: Record<Role, string> = {
  owner: "Owner",
  ops: "Ops",
  support: "Support",
};

// Resolve a user's effective permissions: start from their role defaults, then
// apply per-user overrides (a map of permission -> allowed). An override wins over
// the default; absence of an override means "follow the role default".
export function effectivePermissions(
  role: Role | null | undefined,
  overrides: Record<string, boolean> = {},
): string[] {
  if (!role) return [];
  const defaults = new Set(ROLE_DEFAULTS[role] ?? []);
  return PERMISSION_KEYS.filter((key) => {
    if (Object.prototype.hasOwnProperty.call(overrides, key)) return overrides[key];
    return defaults.has(key);
  });
}

// Is `perm` on by default for `role`? (used to decide whether a toggle is an
// override or a return-to-default, so we can keep the overrides table minimal.)
export function isRoleDefault(role: Role, perm: string): boolean {
  return (ROLE_DEFAULTS[role] ?? []).includes(perm);
}
